import { ColumnState, GridApi } from '@ag-grid-community/core';
import { GROUP_COLUMN_PREFIX } from '../Constants';
import { OrderConfig } from 'common/visualizations/vif';

export const getColumnSortState = (vifOrderConfig: OrderConfig[]) => {
  const columnSortState: ColumnState[] = vifOrderConfig.map((col, orderIndex) => {
    return {
      colId: col.columnName,
      sort: col.ascending ? 'asc' : 'desc',
      sortIndex: orderIndex
    };
  });
  return columnSortState;
};

export interface IColumnGroupResetParams {
  columnState: ColumnState[] | undefined;
  vifOrderConfig: OrderConfig[];
  isIndented: boolean | undefined;
}

export const getGroupedColumnsToResetSort = ({
  columnState,
  vifOrderConfig,
  isIndented
}: IColumnGroupResetParams) => {
  if (columnState === undefined) {
    return [];
  }
  const firstGroup = columnState.find((column) => column.rowGroupIndex == 0);
  return columnState
    .map((col): ColumnState | null => {
      if (
        col.colId &&
        col.sort &&
        shouldResetSort({ colId: col.colId, vifOrderConfig, isIndented, firstGroup })
      ) {
        return {
          colId: col.colId,
          sort: null,
          sortIndex: null
        };
      }
      return null;
    })
    .filter((col): col is ColumnState => col !== null);
};

interface IResetSortParams {
  colId: string;
  vifOrderConfig: OrderConfig[];
  isIndented: boolean | undefined;
  firstGroup: ColumnState | undefined;
}

const shouldResetSort = ({ colId, vifOrderConfig, isIndented, firstGroup }: IResetSortParams) => {
  if (!colId.startsWith(GROUP_COLUMN_PREFIX)) return false;

  // if a column was sorted and is no longer sorted, the map from vif columns to agColumn defs
  // will reset its `sort` attribute properly. but since grouped columns are auto-generated by
  // ag-grid, we need to manually reset their `sort` attribute in the case that the grouped column
  // is no longer marked as sorted on the vif. to do this, we will find any grouped column that
  // ag-grid has marked as sorted, but is not marked as sorted on the vif, and reset its `sort`
  // attribute to null.
  const sortedColumns = new Set(vifOrderConfig.map((o) => o.columnName));
  if (!isIndented) {
    return !sortedColumns.has(colId);
  } else {
    const groupColName = firstGroup?.colId.replace(`${GROUP_COLUMN_PREFIX}-`, '');
    const autoColId = `${GROUP_COLUMN_PREFIX}-${groupColName}`;
    return !sortedColumns.has(autoColId);
  }
};

export const updateColIdForIndentedLayout = (columnState: ColumnState[]) => {
  return columnState.map((column) => ({
    colId: column.colId.startsWith(GROUP_COLUMN_PREFIX) ? GROUP_COLUMN_PREFIX : column.colId,
    sort: column.sort,
    sortIndex: column.sortIndex
  }));
};

interface IGetNonGroupedColumnsToResetSort {
  columnState: ColumnState[] | undefined;
  vifOrderConfig: OrderConfig[];
}

export const getNonGroupedColumnsToResetSort = ({
  columnState,
  vifOrderConfig
}: IGetNonGroupedColumnsToResetSort) => {
  if (columnState === undefined) {
    return [];
  }
  return columnState
    .map((column): ColumnState | null => {
      if (column.colId.startsWith(GROUP_COLUMN_PREFIX)) return null;
      const orderConfig = vifOrderConfig.find((order) => order.columnName === column.colId);
      if (!orderConfig) {
        return {
          colId: column.colId,
          sort: null,
          sortIndex: null
        };
      }
      return null;
    })
    .filter((col): col is ColumnState => col !== null);
};

interface IUpdateColumnSort {
  api: GridApi | undefined;
  vifOrderConfig: OrderConfig[];
  isIndented: boolean | undefined;
}

export const updateColumnsSort = ({ api, vifOrderConfig, isIndented }: IUpdateColumnSort) => {
  let columnSortState = getColumnSortState(vifOrderConfig);
  const columnState = api?.getColumnState();
  const groupedColumnsToResetSort = getGroupedColumnsToResetSort({
    columnState,
    vifOrderConfig,
    isIndented
  });
  const nonGroupedColumnsToResetSort = getNonGroupedColumnsToResetSort({ columnState, vifOrderConfig });

  columnSortState.push(...groupedColumnsToResetSort);
  columnSortState.push(...nonGroupedColumnsToResetSort);

  // Sample column state:
  // [
  //   {
  //     colId: "ag-Grid-AutoColumn-fieldname",
  //     sort: "desc",
  //     sortIndex: 0
  //   }
  // ]
  // We have to manipulate the colId if isIndented is TRUE
  // since in agGrid, it is expected to have colId: ag-Grid-AutoColumn (without fieldname)
  if (isIndented) {
    columnSortState = updateColIdForIndentedLayout(columnSortState);
  }
  api?.applyColumnState({ state: columnSortState });
};
